import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { Node } from './Node';
import { Group } from './Group';
import { Minimap } from './Minimap';
import { ZoomInIcon, ZoomOutIcon, ResetZoomIcon, ExportIcon, ImportIcon, UndoIcon, RedoIcon, RunIcon } from './icons';
import { NODE_WIDTH, NODE_HEIGHT } from '../constants';
import { BusinessPlanNode } from './BusinessPlanNode';

const GRID_SIZE = 20;

const getEdgePath = (
  sourceNode,
  targetNode,
  sourceHandle
) => {
  const sourcePos = { ...sourceNode.position };
  const targetPos = { ...targetNode.position };

  sourcePos.x += NODE_WIDTH;
  targetPos.x -= 6; 

  if (sourceNode.type === 'IF_CONDITION') {
    sourcePos.y += sourceHandle === 'true' ? NODE_HEIGHT / 4 : NODE_HEIGHT * 3 / 4;
  } else if (sourceNode.type === 'LOOP_START') {
    sourcePos.y += sourceHandle === 'loopBody' ? NODE_HEIGHT / 4 : NODE_HEIGHT * 3 / 4;
  } else {
    sourcePos.y += NODE_HEIGHT / 2;
  }
  targetPos.y += NODE_HEIGHT / 2;

  const P0 = sourcePos;
  const P3 = targetPos;
  const curveX = Math.abs(P3.x - P0.x) * 0.6;
  const P1 = { x: P0.x + curveX, y: P0.y };
  const P2 = { x: P3.x - curveX, y: P3.y };
  
  const path = `M ${P0.x} ${P0.y} C ${P1.x} ${P1.y}, ${P2.x} ${P2.y}, ${P3.x} ${P3.y}`;
  
  const t = 0.5;
  const midPoint = {
    x: Math.pow(1 - t, 3) * P0.x + 3 * Math.pow(1 - t, 2) * t * P1.x + 3 * (1 - t) * t * t * P2.x + Math.pow(t, 3) * P3.x,
    y: Math.pow(1 - t, 3) * P0.y + 3 * Math.pow(1 - t, 2) * t * P1.y + 3 * (1 - t) * t * t * P2.y + Math.pow(t, 3) * P3.y,
  };

  let label = null;
  if (sourceNode.type === 'IF_CONDITION') {
    if (sourceHandle === 'true') label = 'True';
    if (sourceHandle === 'false') label = 'False';
  } else if (sourceNode.type === 'LOOP_START') {
    if (sourceHandle === 'loopBody') label = 'Loop Body';
    if (sourceHandle === 'afterLoop') label = 'After Loop';
  }
  return { path, label, midPoint };
};

export const WorkflowCanvas = ({
  nodes,
  edges,
  groups,
  onNodeClick,
  onCanvasClick,
  onSelectNodes,
  selectedNodeIds,
  onNodePositionChange,
  onNodeDragStart,
  onNodeDragStop,
  onGroupPositionChange,
  onGroupDragStart,
  onGroupDragStop,
  onToggleGroupCollapse,
  onDeleteNode,
  onAddEdge,
  onExport,
  onImport,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onRun,
  executionStatus,
  activeNodeId,
  onOpenDelegationPanel,
  onAddComment, // New prop for adding comments
  nodeComments = {}, // New prop for node comments
  executionLogs = [], // New prop for execution logs
  setState, // Add setState prop for clear canvas functionality
  onGroupNodes // Add onGroupNodes prop for grouping functionality
}) => {
  const canvasRef = useRef(null);
  const [viewTransform, setViewTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
  const [draggingItem, setDraggingItem] = useState(null);
  const [drawingEdge, setDrawingEdge] = useState(null);
  const [hoveredInputTargetId, setHoveredInputTargetId] = useState(null);
  const [marqueeRect, setMarqueeRect] = useState(null);
  const [showControls, setShowControls] = useState(true); // Add state for showing/hiding controls
  const [isDragOver, setIsDragOver] = useState(false); // Add state for drag over effect
  const isMarqueeSelectingRef = useRef(false);

  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setCanvasDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    
    resizeObserver.observe(canvasEl);
    return () => resizeObserver.disconnect();
  }, []);


  const handleMouseDown = useCallback((e) => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    
    // Panning
    if (e.altKey || e.button === 1) {
      e.preventDefault();
      canvasEl.classList.add('select-none');
      const startPos = { x: e.clientX, y: e.clientY };
      const startTransform = { ...viewTransform };

      const handleMouseMove = (moveEvent) => {
        const dx = moveEvent.clientX - startPos.x;
        const dy = moveEvent.clientY - startPos.y;
        setViewTransform({
          ...startTransform,
          x: startTransform.x + dx,
          y: startTransform.y + dy,
        });
      };

      const handleMouseUp = () => {
        canvasEl.classList.remove('select-none');
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    // Marquee Selection / Clear selection
    else if (e.target === e.currentTarget && e.button === 0) {
      const rect = canvasEl.getBoundingClientRect();
      const startX = e.clientX - rect.left;
      const startY = e.clientY - rect.top;

      setMarqueeRect({ startX, startY, endX: startX, endY: startY });

      const handleMouseMove = (moveEvent) => {
          if (!isMarqueeSelectingRef.current) {
              const dx = Math.abs(moveEvent.clientX - rect.left - startX);
              const dy = Math.abs(moveEvent.clientY - rect.top - startY);
              if (dx > 5 || dy > 5) { // Threshold to differentiate click from drag
                  isMarqueeSelectingRef.current = true;
                  canvasEl.classList.add('select-none');
              }
          }
          
          if (isMarqueeSelectingRef.current) {
              const currentX = moveEvent.clientX - rect.left;
              const currentY = moveEvent.clientY - rect.top;
              setMarqueeRect(prev => prev ? { ...prev, endX: currentX, endY: currentY } : null);
          }
      };

      const handleMouseUp = (upEvent) => {
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('mouseup', handleMouseUp);
          canvasEl.classList.remove('select-none');

          if (isMarqueeSelectingRef.current && marqueeRect) {
              const marqueeStart = {
                  x: (Math.min(marqueeRect.startX, marqueeRect.endX) - viewTransform.x) / viewTransform.scale,
                  y: (Math.min(marqueeRect.startY, marqueeRect.endY) - viewTransform.y) / viewTransform.scale
              };
              const marqueeEnd = {
                  x: (Math.max(marqueeRect.startX, marqueeRect.endX) - viewTransform.x) / viewTransform.scale,
                  y: (Math.max(marqueeRect.startY, marqueeRect.endY) - viewTransform.y) / viewTransform.scale
              };

              const selectedIds = nodes.filter(node => {
                  const nodeRect = {
                      x: node.position.x,
                      y: node.position.y,
                      width: NODE_WIDTH,
                      height: NODE_HEIGHT
                  };
                  return marqueeStart.x < nodeRect.x + nodeRect.width &&
                         marqueeEnd.x > nodeRect.x &&
                         marqueeStart.y < nodeRect.y + nodeRect.height &&
                         marqueeEnd.y > nodeRect.y;
              }).map(node => node.id);

              onSelectNodes(selectedIds, upEvent.shiftKey);
          } else {
              onCanvasClick();
          }

          setMarqueeRect(null);
          isMarqueeSelectingRef.current = false;
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
  }, [viewTransform, onCanvasClick, onSelectNodes, nodes]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const { deltaY } = e;
    const scaleFactor = 1.1;
    const newScale = deltaY < 0 ? viewTransform.scale * scaleFactor : viewTransform.scale / scaleFactor;
    const clampedScale = Math.max(0.2, Math.min(2, newScale));

    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    const mousePoint = {
        x: (mouseX - viewTransform.x) / viewTransform.scale,
        y: (mouseY - viewTransform.y) / viewTransform.scale
    };

    setViewTransform({
      scale: clampedScale,
      x: mouseX - mousePoint.x * clampedScale,
      y: mouseY - mousePoint.y * clampedScale,
    });
  }, [viewTransform]);

  const handleNodeMouseDown = useCallback((e, nodeId) => {
    if (e.button !== 0) return;
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    
    onNodeDragStart(nodeId);
    canvasRef.current.classList.add('select-none');

    const startX = e.clientX / viewTransform.scale;
    const startY = e.clientY / viewTransform.scale;

    setDraggingItem({
        type: 'node',
        id: nodeId,
        offset: { x: startX - node.position.x, y: startY - node.position.y },
    });
  }, [nodes, viewTransform.scale, onNodeDragStart]);

  const handleGroupMouseDown = useCallback((e, groupId) => {
    if (e.button !== 0) return;
    const group = groups.find(g => g.id === groupId);
    if (!group) return;

    onGroupDragStart(groupId);
    canvasRef.current.classList.add('select-none');
    
    const startX = e.clientX / viewTransform.scale;
    const startY = e.clientY / viewTransform.scale;
    
    setDraggingItem({
        type: 'group',
        id: groupId,
        offset: { x: startX - group.position.x, y: startY - group.position.y },
    });
  }, [groups, viewTransform.scale, onGroupDragStart]);

  const handleStartDrawingEdge = useCallback((sourceId, sourceHandle, startPosition) => {
    const sourceNode = nodes.find(n => n.id === sourceId);
    if (!sourceNode) return;
    
    let handlePos = { 
        x: sourceNode.position.x + 208, 
        y: sourceNode.position.y + 40
    };

    if (sourceNode.type === 'IF_CONDITION') {
        handlePos.y = sourceNode.position.y + (sourceHandle === 'true' ? 20 : 60);
    } else if (sourceNode.type === 'LOOP_START') {
        handlePos.y = sourceNode.position.y + (sourceHandle === 'loopBody' ? 20 : 60);
    }

    setDrawingEdge({
      sourceId,
      sourceHandle,
      startPos: handlePos,
      endPos: handlePos,
    });
  }, [nodes]);

  const handleMouseUpOnNodeInput = useCallback((targetId) => {
    if (drawingEdge) {
      if (drawingEdge.sourceId !== targetId) {
        onAddEdge({
          source: drawingEdge.sourceId,
          target: targetId,
          sourceHandle: drawingEdge.sourceHandle
        });
      }
      setDrawingEdge(null);
      setHoveredInputTargetId(null);
    }
  }, [drawingEdge, onAddEdge]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (draggingItem) {
          const newX = e.clientX / viewTransform.scale - draggingItem.offset.x;
          const newY = e.clientY / viewTransform.scale - draggingItem.offset.y;
          const snappedX = Math.round(newX / GRID_SIZE) * GRID_SIZE;
          const snappedY = Math.round(newY / GRID_SIZE) * GRID_SIZE;
          if (draggingItem.type === 'node') {
            onNodePositionChange(draggingItem.id, { x: snappedX, y: snappedY });
          } else {
            onGroupPositionChange(draggingItem.id, { x: snappedX, y: snappedY });
          }
      }
      if (drawingEdge) {
          const rect = canvasRef.current.getBoundingClientRect();
          const canvasX = (e.clientX - rect.left - viewTransform.x) / viewTransform.scale;
          const canvasY = (e.clientY - rect.top - viewTransform.y) / viewTransform.scale;
          setDrawingEdge(prev => prev ? { ...prev, endPos: { x: canvasX, y: canvasY } } : null);
      }
    };
    
    const handleMouseUp = () => {
      if (draggingItem) {
        if(draggingItem.type === 'node') onNodeDragStop();
        else onGroupDragStop();
        setDraggingItem(null);
        canvasRef.current.classList.remove('select-none');
      }
      if (drawingEdge) {
        setDrawingEdge(null);
        setHoveredInputTargetId(null);
      }
    };

    if (draggingItem || drawingEdge) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingItem, onNodePositionChange, onNodeDragStop, onGroupPositionChange, onGroupDragStop, viewTransform, drawingEdge]);

  const renderedEdges = useMemo(() => {
    const nodeMap = new Map(nodes.map(node => [node.id, node]));
    return edges.map(edge => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);
      if (!sourceNode || !targetNode) return null;

      const { path, label, midPoint } = getEdgePath(sourceNode, targetNode, edge.sourceHandle);
      
      // Determine edge color based on source handle or type
      let strokeColor = '#94a3b8'; // Default color
      if (edge.sourceHandle === 'true') strokeColor = '#22c55e'; // Green for true paths
      if (edge.sourceHandle === 'false') strokeColor = '#ef4444'; // Red for false paths
      if (edge.sourceHandle === 'loopBody') strokeColor = '#a855f7'; // Purple for loop body
      if (edge.sourceHandle === 'afterLoop') strokeColor = '#6b7280'; // Gray for after loop

      return (
        <g key={edge.id}>
          <path 
            d={path} 
            stroke={strokeColor} 
            strokeWidth="2" 
            fill="none" 
            markerEnd="url(#arrowhead)"
          />
          {label && (
            <>
              <text 
                x={midPoint.x} 
                y={midPoint.y} 
                textAnchor="middle" 
                dy=".3em" 
                stroke="#111827" 
                strokeWidth="4" 
                strokeLinejoin="round" 
                style={{ fontSize: '12px', fontWeight: 'bold' }}
              >
                {label}
              </text>
              <text 
                x={midPoint.x} 
                y={midPoint.y} 
                textAnchor="middle" 
                dy=".3em" 
                fill={strokeColor} 
                style={{ fontSize: '12px', fontWeight: 'bold' }}
              >
                {label}
              </text>
            </>
          )}
        </g>
      );
    }).filter(Boolean);
  }, [nodes, edges]);

  const renderedLoopFeedbackLines = useMemo(() => {
    const loopStarts = nodes.filter(n => n.type === 'LOOP_START');
    const loopEnds = nodes.filter(n => n.type === 'LOOP_END');
    const lines = [];

    if (loopStarts.length > 0 && loopEnds.length > 0) {
        for (const endNode of loopEnds) {
            const potentialStarts = loopStarts
                .filter(startNode => startNode.position.y <= endNode.position.y)
                .sort((a,b) => endNode.position.y - a.position.y);
            
            if (potentialStarts.length > 0) {
                const startNode = potentialStarts[0];
                const start = { x: startNode.position.x + 104, y: startNode.position.y - 6 };
                const end = { x: endNode.position.x + 104, y: endNode.position.y + 56 };
                const midY = end.y + 40;
                const path = `M ${end.x} ${end.y} C ${end.x} ${midY}, ${start.x} ${midY}, ${start.x} ${start.y}`;
                lines.push(<path key={`loop-${startNode.id}-${endNode.id}`} d={path} stroke="#6b7280" strokeWidth="2" fill="none" strokeDasharray="5 5" />);
            }
        }
    }
    return lines;
  }, [nodes]);

  const collapsedNodeIds = useMemo(() => 
    new Set(groups.filter(g => g.isCollapsed).flatMap(g => g.nodeIds)),
    [groups]
  );
  const visibleNodes = useMemo(() => nodes.filter(n => !collapsedNodeIds.has(n.id)), [nodes, collapsedNodeIds]);
  const visibleEdges = useMemo(() => edges.filter(edge => 
    !collapsedNodeIds.has(edge.source) && !collapsedNodeIds.has(edge.target)
  ), [edges, collapsedNodeIds]);
  const visibleGroups = useMemo(() => groups.filter(g => !g.isCollapsed), [groups]);
  
  // Create nodeMap for edge rendering
  const nodeMap = useMemo(() => {
    const map = new Map();
    nodes.forEach(node => map.set(node.id, node));
    return map;
  }, [nodes]);

  // Group execution logs by node ID for easy access
  const nodeExecutionLogs = useMemo(() => {
    const logsByNode = {};
    executionLogs.forEach(log => {
      if (log.nodeId) {
        if (!logsByNode[log.nodeId]) {
          logsByNode[log.nodeId] = [];
        }
        logsByNode[log.nodeId].push(log);
      }
    });
    return logsByNode;
  }, [executionLogs]);

  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    
    // Add wheel event listener with passive option set to false to allow preventDefault
    canvasEl.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      canvasEl.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Add keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Toggle controls with Tab key
      if (e.key === 'Tab' && e.ctrlKey) {
        e.preventDefault();
        setShowControls(!showControls);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showControls]);

  // Add drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    // Check if we're actually leaving the canvas (not moving to a child element)
    if (e.relatedTarget === null || !canvasRef.current?.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const file = e.dataTransfer.files?.[0];
    if (!file || file.type !== 'application/json') {
      alert('Please drop a valid JSON file.');
      return;
    }

    try {
      const content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = () => reject(new Error('Failed to read file.'));
        reader.readAsText(file);
      });
      
      const data = JSON.parse(content);
      
      // Validate the structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid file format. Expected a JSON object.');
      }
      
      // Check if it's a workflow file or a general JSON
      let workflowData;
      if (Array.isArray(data.nodes) && Array.isArray(data.edges)) {
        // It's a workflow file
        workflowData = data;
      } else {
        // Try to parse as a general workflow object
        workflowData = data.workflow || data;
        if (!Array.isArray(workflowData.nodes) || !Array.isArray(workflowData.edges)) {
          throw new Error('Invalid workflow file structure. Missing nodes or edges.');
        }
      }
      
      // Emit a custom event to handle the import in WorkflowApp
      const event = new CustomEvent('workflowImportDropped', { detail: { workflowData, fileName: file.name } });
      window.dispatchEvent(event);
    } catch (err) {
      console.error("Failed to process dropped file:", err);
      alert(`Failed to process file: ${err.message}`);
    }
  }, []);

  return (
    <div 
      ref={canvasRef}
      className={`relative flex-1 overflow-hidden bg-dark-bg ${isDragOver ? 'bg-dark-surface/50' : ''}`}
      style={{ 
        backgroundImage: `radial-gradient(circle, #334155 1px, transparent 1px)`,
        backgroundSize: `${GRID_SIZE * viewTransform.scale}px ${GRID_SIZE * viewTransform.scale}px`,
        backgroundPosition: `${viewTransform.x}px ${viewTransform.y}px`
      }}
      onMouseDown={handleMouseDown}
      onWheel={handleWheel}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag and drop overlay */}
      {isDragOver && (
        <div className="absolute inset-0 bg-brand-primary/20 border-2 border-dashed border-brand-primary flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-dark-surface p-6 rounded-lg shadow-xl text-center">
            <ImportIcon className="w-12 h-12 text-brand-primary mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Drop Workflow File</h3>
            <p className="text-dark-text-secondary">Drop a JSON workflow file to import it into a new workspace</p>
          </div>
        </div>
      )}

      <div 
        className="absolute inset-0 origin-top-left"
        style={{
          transform: `translate(${viewTransform.x}px, ${viewTransform.y}px) scale(${viewTransform.scale})`,
          backgroundImage: 'radial-gradient(circle, #374151 1px, transparent 1px)',
          backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
        }}
      >
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          {/* Define arrowhead markers */}
          <defs>
            <marker 
              id="arrowhead" 
              markerWidth="10" 
              markerHeight="7" 
              refX="9" 
              refY="3.5" 
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
            </marker>
            <marker 
              id="arrowhead-green" 
              markerWidth="10" 
              markerHeight="7" 
              refX="9" 
              refY="3.5" 
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#22c55e" />
            </marker>
            <marker 
              id="arrowhead-red" 
              markerWidth="10" 
              markerHeight="7" 
              refX="9" 
              refY="3.5" 
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#ef4444" />
            </marker>
            <marker 
              id="arrowhead-purple" 
              markerWidth="10" 
              markerHeight="7" 
              refX="9" 
              refY="3.5" 
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#a855f7" />
            </marker>
            <marker 
              id="arrowhead-gray" 
              markerWidth="10" 
              markerHeight="7" 
              refX="9" 
              refY="3.5" 
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
            </marker>
          </defs>
          
          {visibleEdges.map(edge => {
            const sourceNode = nodeMap.get(edge.source);
            const targetNode = nodeMap.get(edge.target);
            if (!sourceNode || !targetNode) return null;
            
            const { path, label, midPoint } = getEdgePath(sourceNode, targetNode, edge.sourceHandle);
            
            // Determine edge color and marker based on source handle or type
            let strokeColor = '#94a3b8'; // Default color
            let markerId = 'arrowhead';
            if (edge.sourceHandle === 'true') {
              strokeColor = '#22c55e'; // Green for true paths
              markerId = 'arrowhead-green';
            }
            if (edge.sourceHandle === 'false') {
              strokeColor = '#ef4444'; // Red for false paths
              markerId = 'arrowhead-red';
            }
            if (edge.sourceHandle === 'loopBody') {
              strokeColor = '#a855f7'; // Purple for loop body
              markerId = 'arrowhead-purple';
            }
            if (edge.sourceHandle === 'afterLoop') {
              strokeColor = '#6b7280'; // Gray for after loop
              markerId = 'arrowhead-gray';
            }
            
            return (
              <g key={edge.id}>
                <path
                  d={path}
                  stroke={strokeColor}
                  strokeWidth="2"
                  fill="none"
                  markerEnd={`url(#${markerId})`}
                />
                {label && (
                  <text
                    x={midPoint.x}
                    y={midPoint.y - 5}
                    textAnchor="middle"
                    className="text-xs fill-slate-400 pointer-events-none select-none"
                    style={{ fontSize: '10px' }}
                  >
                    {label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {visibleGroups.map(group => (
          <Group
            key={group.id}
            group={group}
            isSelected={selectedNodeIds.includes(group.id)}
            onMouseDown={handleGroupMouseDown}
            onToggleCollapse={() => onToggleGroupCollapse(group.id)}
          />
        ))}
        {visibleNodes.map(node => (
          <BusinessPlanNode
            key={node.id}
            node={node}
            onClick={onNodeClick}
            isSelected={selectedNodeIds.includes(node.id)}
            onMouseDown={handleNodeMouseDown}
            onDelete={onDeleteNode}
            onStartDrawingEdge={handleStartDrawingEdge}
            onMouseUpOnInput={handleMouseUpOnNodeInput}
            isDrawingEdge={!!drawingEdge}
            drawingEdgeSourceId={drawingEdge?.sourceId || null}
            hoveredInputTargetId={hoveredInputTargetId}
            onSetHoveredInputTarget={setHoveredInputTargetId}
            isExecuting={activeNodeId === node.id}
            onOpenDelegationPanel={onOpenDelegationPanel}
            onAddComment={onAddComment}
            comments={nodeComments[node.id] || []}
            executionLogs={nodeExecutionLogs[node.id] || []}
            nodes={nodes}
          />
        ))}
        
        {/* Welcome message when canvas is empty */}
        {nodes.length === 0 && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center text-slate-400">
            <div className="text-2xl font-bold mb-4">Workflow Canvas</div>
            <p className="mb-2">Describe your business plan in the header input to generate a workflow</p>
            <p className="text-sm">Or add nodes manually from the sidebar</p>
          </div>
        )}
      </div>
      {marqueeRect && (
          <div
              className="absolute border border-emerald-500 bg-emerald-500/20 pointer-events-none"
              style={{
                  left: Math.min(marqueeRect.startX, marqueeRect.endX),
                  top: Math.min(marqueeRect.startY, marqueeRect.endY),
                  width: Math.abs(marqueeRect.startX - marqueeRect.endX),
                  height: Math.abs(marqueeRect.startY - marqueeRect.endY),
              }}
          />
      )}
      <Minimap 
        nodes={nodes}
        edges={edges}
        viewTransform={viewTransform}
        canvasDimensions={canvasDimensions}
        onPan={setViewTransform}
      />
      
      {/* Desktop controls */}
      {showControls && (
        <div className="absolute bottom-4 right-4 bg-slate-800/50 backdrop-blur-md p-1 rounded-md shadow-lg flex items-center gap-1 border border-slate-700/50 z-10 hidden sm:flex">
          <button onClick={onRun} disabled={executionStatus === 'RUNNING'} title="Run Workflow" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"><RunIcon className="w-5 h-5"/></button>
          <div className="w-px h-6 bg-slate-700 mx-1"></div>
          <button onClick={() => {
            const allNodeIds = nodes.map(node => node.id);
            onSelectNodes(allNodeIds, false);
          }} title="Select All Nodes" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
          </button>
          <button onClick={() => {
            // Group selected nodes
            if (selectedNodeIds.length > 1) {
              onGroupNodes();
            } else {
              alert('Please select at least 2 nodes to group');
            }
          }} title="Group Selected Nodes" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </button>
          <button onClick={() => {
            // Clear all nodes and edges
            if (window.confirm('Are you sure you want to clear the canvas?')) {
              setState({ nodes: [], edges: [], groups: [] });
            }
          }} title="Clear Canvas" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <button onClick={() => {
            // Fit view to show all nodes
            if (nodes.length > 0) {
              // Group nodes by type for better organization
              const regularNodes = nodes.filter(n => !['TODO_LIST', 'TODO_VIEW'].includes(n.type));
              const todoNodes = nodes.filter(n => n.type === 'TODO_LIST');
              const todoViewNodes = nodes.filter(n => n.type === 'TODO_VIEW');
              
              // Calculate bounds for regular nodes (main flow)
              let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
              
              if (regularNodes.length > 0) {
                regularNodes.forEach(node => {
                  minX = Math.min(minX, node.position.x);
                  minY = Math.min(minY, node.position.y);
                  maxX = Math.max(maxX, node.position.x + NODE_WIDTH);
                  maxY = Math.max(maxY, node.position.y + NODE_HEIGHT);
                });
              }
              
              // Add some padding
              const padding = 150;
              minX = regularNodes.length > 0 ? minX - padding : 0;
              minY = regularNodes.length > 0 ? minY - padding : 0;
              maxX = regularNodes.length > 0 ? maxX + padding : NODE_WIDTH + padding;
              maxY = regularNodes.length > 0 ? maxY + padding : NODE_HEIGHT + padding;
              
              // Adjust for TODO nodes if they exist
              if (todoNodes.length > 0) {
                let todoMinY = Infinity, todoMaxY = -Infinity;
                todoNodes.forEach(node => {
                  todoMinY = Math.min(todoMinY, node.position.y);
                  todoMaxY = Math.max(todoMaxY, node.position.y + NODE_HEIGHT);
                });
                maxY = Math.max(maxY, todoMaxY + padding);
              }
              
              const width = maxX - minX;
              const height = maxY - minY;
              
              const scaleX = canvasDimensions.width / width;
              const scaleY = canvasDimensions.height / height;
              const scale = Math.min(scaleX, scaleY, 1); // Don't zoom in more than 100%
              
              const centerX = (minX + maxX) / 2;
              const centerY = (minY + maxY) / 2;
              
              const newViewX = canvasDimensions.width / 2 - centerX * scale;
              const newViewY = canvasDimensions.height / 2 - centerY * scale;
              
              setViewTransform({
                x: newViewX,
                y: newViewY,
                scale: scale
              });
            }
          }} title="Fit View" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 0h-4m4 0l-5-5" />
            </svg>
          </button>
          <div className="w-px h-6 bg-slate-700 mx-1"></div>
          <button onClick={onUndo} disabled={!canUndo} title="Undo (Ctrl+Z)" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"><UndoIcon className="w-5 h-5"/></button>
          <button onClick={onRedo} disabled={!canRedo} title="Redo (Ctrl+Y)" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"><RedoIcon className="w-5 h-5"/></button>
          <div className="w-px h-6 bg-slate-700 mx-1"></div>
          <button onClick={() => setViewTransform(v => ({...v, scale: Math.min(2, v.scale * 1.2)}))} className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white"><ZoomInIcon className="w-5 h-5"/></button>
          <span className="text-xs font-mono text-slate-400 w-10 text-center">{(viewTransform.scale * 100).toFixed(0)}%</span>
          <button onClick={() => setViewTransform(v => ({...v, scale: Math.max(0.2, v.scale / 1.2)}))} className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white"><ZoomOutIcon className="w-5 h-5"/></button>
          <button onClick={() => setViewTransform({x:0, y:0, scale:1})} className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white"><ResetZoomIcon className="w-5 h-5"/></button>
          <div className="w-px h-6 bg-slate-700 mx-1"></div>
          <button onClick={onImport} title="Import Workflow" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white"><ImportIcon className="w-5 h-5"/></button>
          <button onClick={onExport} title="Export Workflow" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white"><ExportIcon className="w-5 h-5"/></button>
        </div>
      )}
      
      {/* Toggle controls button */}
      <button 
        onClick={() => setShowControls(!showControls)}
        className="absolute bottom-4 right-4 bg-slate-800/50 backdrop-blur-md p-1.5 rounded-md shadow-lg border border-slate-700/50 z-10 sm:hidden hover:bg-slate-700 text-slate-300 hover:text-white"
        title={showControls ? "Hide Controls" : "Show Controls"}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </button>
      
      {/* Mobile controls */}
      {showControls && (
        <div className="absolute bottom-4 right-4 bg-slate-800/50 backdrop-blur-md p-1 rounded-md shadow-lg flex items-center gap-1 border border-slate-700/50 z-10 sm:hidden">
          <button onClick={onRun} disabled={executionStatus === 'RUNNING'} title="Run Workflow" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent">
            <RunIcon className="w-5 h-5"/>
          </button>
          <div className="w-px h-6 bg-slate-700 mx-1"></div>
          <button onClick={() => {
            const allNodeIds = nodes.map(node => node.id);
            onSelectNodes(allNodeIds, false);
          }} title="Select All Nodes" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
          </button>
          <button onClick={() => {
            // Group selected nodes
            if (selectedNodeIds.length > 1) {
              onGroupNodes();
            } else {
              alert('Please select at least 2 nodes to group');
            }
          }} title="Group Selected Nodes" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </button>
          <button onClick={() => {
            // Clear all nodes and edges
            if (window.confirm('Are you sure you want to clear the canvas?')) {
              setState({ nodes: [], edges: [], groups: [] });
            }
          }} title="Clear Canvas" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <button onClick={() => {
            // Fit view to show all nodes
            if (nodes.length > 0) {
              // Group nodes by type for better organization
              const regularNodes = nodes.filter(n => !['TODO_LIST', 'TODO_VIEW'].includes(n.type));
              const todoNodes = nodes.filter(n => n.type === 'TODO_LIST');
              const todoViewNodes = nodes.filter(n => n.type === 'TODO_VIEW');
              
              // Calculate bounds for regular nodes (main flow)
              let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
              
              if (regularNodes.length > 0) {
                regularNodes.forEach(node => {
                  minX = Math.min(minX, node.position.x);
                  minY = Math.min(minY, node.position.y);
                  maxX = Math.max(maxX, node.position.x + NODE_WIDTH);
                  maxY = Math.max(maxY, node.position.y + NODE_HEIGHT);
                });
              }
              
              // Add some padding
              const padding = 150;
              minX = regularNodes.length > 0 ? minX - padding : 0;
              minY = regularNodes.length > 0 ? minY - padding : 0;
              maxX = regularNodes.length > 0 ? maxX + padding : NODE_WIDTH + padding;
              maxY = regularNodes.length > 0 ? maxY + padding : NODE_HEIGHT + padding;
              
              // Adjust for TODO nodes if they exist
              if (todoNodes.length > 0) {
                let todoMinY = Infinity, todoMaxY = -Infinity;
                todoNodes.forEach(node => {
                  todoMinY = Math.min(todoMinY, node.position.y);
                  todoMaxY = Math.max(todoMaxY, node.position.y + NODE_HEIGHT);
                });
                maxY = Math.max(maxY, todoMaxY + padding);
              }
              
              const width = maxX - minX;
              const height = maxY - minY;
              
              const scaleX = canvasDimensions.width / width;
              const scaleY = canvasDimensions.height / height;
              const scale = Math.min(scaleX, scaleY, 1); // Don't zoom in more than 100%
              
              const centerX = (minX + maxX) / 2;
              const centerY = (minY + maxY) / 2;
              
              const newViewX = canvasDimensions.width / 2 - centerX * scale;
              const newViewY = canvasDimensions.height / 2 - centerY * scale;
              
              setViewTransform({
                x: newViewX,
                y: newViewY,
                scale: scale
              });
            }
          }} title="Fit View" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 0h-4m4 0l-5-5" />
            </svg>
          </button>
          <div className="w-px h-6 bg-slate-700 mx-1"></div>
          <button onClick={onUndo} disabled={!canUndo} title="Undo" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent">
            <UndoIcon className="w-5 h-5"/>
          </button>
          <button onClick={onRedo} disabled={!canRedo} title="Redo" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent">
            <RedoIcon className="w-5 h-5"/>
          </button>
          <div className="w-px h-6 bg-slate-700 mx-1"></div>
          <button onClick={onImport} title="Import Workflow" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <ImportIcon className="w-5 h-5"/>
          </button>
          <button onClick={onExport} title="Export Workflow" className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <ExportIcon className="w-5 h-5"/>
          </button>
        </div>
      )}

      {/* Mobile zoom controls */}
      {showControls && (
        <div className="absolute bottom-20 right-4 bg-slate-800/50 backdrop-blur-md p-1 rounded-md shadow-lg flex flex-col items-center gap-1 border border-slate-700/50 z-10 sm:hidden">
          <button onClick={() => setViewTransform(v => ({...v, scale: Math.min(2, v.scale * 1.2)}))} className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <ZoomInIcon className="w-5 h-5"/>
          </button>
          <span className="text-xs font-mono text-slate-400 w-10 text-center">{(viewTransform.scale * 100).toFixed(0)}%</span>
          <button onClick={() => setViewTransform(v => ({...v, scale: Math.max(0.2, v.scale / 1.2)}))} className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <ZoomOutIcon className="w-5 h-5"/>
          </button>
          <button onClick={() => setViewTransform({x:0, y:0, scale:1})} className="p-1.5 hover:bg-slate-700 rounded-md text-slate-300 hover:text-white">
            <ResetZoomIcon className="w-5 h-5"/>
          </button>
        </div>
      )}

    </div>
  );
};